import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { setRollset } from "@/pages/rollshop/repositories/RollsetRepository";
import { Roll } from "@/shared/models/Roll";
import { RollSelector, SectionSelector } from "@/shared/selectors";
import { useRolls } from "@/shared/selectors/roll/RollSelector";
import { pageBgColor } from "@/utils/theme";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Box, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { RollsetInfo } from "./TableRollset";

export const getDiameterError = ({
	topDiameter,
	bottomDiameter,
}: {
	topDiameter: number | null;
	bottomDiameter: number | null;
}) => {
	return topDiameter && bottomDiameter && topDiameter !== bottomDiameter
		? `The diameters are different`
		: undefined;
};

export const getCompatiblePassNumbers = (
	roll: Roll | undefined,
	sectionId: number,
): number[] => {
	if (!roll || roll.RollTypeCode !== "E") return [];
	return roll.Passes.filter((pass) =>
		pass.Sections.some((s) => s.SectionId === sectionId),
	).map((pass) => pass.PassNumber);
};

export const getPassCompatibilityError = (
	topRoll: Roll | undefined,
	bottomRoll: Roll | undefined,
	sectionId: number,
): string | undefined => {
	if (!topRoll || !bottomRoll) return undefined;

	const topCompatiblePasses = getCompatiblePassNumbers(topRoll, sectionId);
	const bottomCompatiblePasses = getCompatiblePassNumbers(
		bottomRoll,
		sectionId,
	);

	const commonPasses = topCompatiblePasses.filter((p) =>
		bottomCompatiblePasses.includes(p),
	);

	const topPassesMessage =
		topCompatiblePasses.length == 0
			? "dont support section on any pass"
			: "supports section on pass " + topCompatiblePasses.join(", ");
	const bottomPassesMessage =
		bottomCompatiblePasses.length == 0
			? "dont support section on any pass"
			: "supports section on pass " + bottomCompatiblePasses.join(", ");

	if (commonPasses.length === 0) {
		return `No matching pass found for section. Top Roll '${topRoll.PartName}' ${topPassesMessage} and Bottom Roll '${bottomRoll.PartName}' ${bottomPassesMessage}. Both rolls must share the same pass number for the selected section.`;
	}

	return undefined;
};

type Props = {
	sectionId: number;
	rollset: RollsetInfo | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const RollsetAssemblyModal = ({
	sectionId,
	rollset,
	show,
	onHide,
}: Props) => {
	const [message, setNotificationMessage] = useState("");
	const [error, setError] = useState("");
	const [confirmMessage, setConfirmMessage] = useState("");
	const [initialTopFields, setInitialTopFields] = useState<{
		diameter: number | null;
		standId: number | null;
	}>({ diameter: null, standId: null });
	const [initialBottomFields, setInitialBottomFields] = useState<{
		diameter: number | null;
		standId: number | null;
	}>({ diameter: null, standId: null });

	const { data: rolls } = useRolls({
		sectionId,
		onError: (error) => setError(error.message),
	});

	const {
		control,
		reset,
		watch,
		trigger,
		setValue,
		getValues,
		formState: { dirtyFields },
		handleSubmit,
	} = useForm<RollsetInfo>({
		mode: "onChange",
	});

	const topRollId = watch("TopRollId");
	const topRollDiameter = watch("TopRollDiameter");
	const topRollType = watch("TopRollType");
	const bottomRollId = watch("BottomRollId");
	const bottomRollDiameter = watch("BottomRollDiameter");
	const bottomRollType = watch("BottomRollType");

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);
	const bothRollsDefined = topRollId != null && bottomRollId != null;

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(setRollset, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["rollsets", sectionId],
			});
			await queryClient.invalidateQueries({
				queryKey: ["readyRollsets", sectionId],
			});
			setNotificationMessage(`The rollset was upsert successfuly`);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});
	const upsertRollMutation = useMutation(upsertRoll, {
		onSuccess: async () => {
			setNotificationMessage("The roll was upsert successfuly");
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const saveData = async (data: RollsetInfo) => {
		const topDiameterChanged =
			data.TopRollDiameter !== initialTopFields.diameter;
		const topStandChanged = rollset?.StandId !== initialTopFields.standId;

		if (topDiameterChanged || topStandChanged) {
			await upsertRollMutation.mutateAsync({
				partId: data.TopRollId!,
				diameter: data.TopRollDiameter,
				standId: rollset?.StandId,
			});
		}

		const bottomDiameterChanged =
			data.BottomRollDiameter !== initialBottomFields.diameter;
		const bottomStandChanged = rollset?.StandId !== initialBottomFields.standId;

		if (bottomDiameterChanged || bottomStandChanged) {
			await upsertRollMutation.mutateAsync({
				partId: data.BottomRollId!,
				diameter: data.BottomRollDiameter,
				standId: rollset?.StandId,
			});
		}

		if (
			data.TopRollId !== rollset?.TopRollId ||
			data.BottomRollId !== rollset?.BottomRollId
		) {
			await upsertMutation.mutateAsync({
				sectionId,
				standId: rollset!.StandId,
				letter: rollset!.Letter,
				topRollId: data.TopRollId,
				bottomRollId: data.BottomRollId,
			});
		} else {
			onHide(true);
		}
	};

	const handleConfirmationClose = (result?: string) => {
		setConfirmMessage("");
		if (result === "OK") {
			saveData(getValues());
		}
	};

	const onSubmit: SubmitHandler<RollsetInfo> = async (data: RollsetInfo) => {
		if (topRollType == "E" && bottomRollType == "E") {
			const topRoll = rolls?.find((r) => r.PartId === topRollId);
			const bottomRoll = rolls?.find((r) => r.PartId === bottomRollId);
			const passError = getPassCompatibilityError(
				topRoll,
				bottomRoll,
				sectionId,
			);

			if (passError) {
				setError(passError);
				return;
			}
		}

		const diameterError = getDiameterError({
			topDiameter: data.TopRollDiameter,
			bottomDiameter: data.BottomRollDiameter,
		});

		if (diameterError) {
			setConfirmMessage(
				`The diameters are different (Top: ${data.TopRollDiameter}, Bottom: ${data.BottomRollDiameter}). Do you want to continue?`,
			);
			return;
		}

		saveData(data);
	};

	useEffect(() => {
		if (show) {
			reset({
				Letter: rollset!.Letter,
				TopRollId: rollset!.TopRollId,
				TopRollDiameter: rollset?.TopRollDiameter,
				TopRollType: rollset?.TopRollType,
				BottomRollId: rollset!.BottomRollId,
				BottomRollDiameter: rollset?.BottomRollDiameter,
				BottomRollType: rollset?.BottomRollType,
				StandId: rollset!.StandId,
			});

			setInitialTopFields({
				diameter: rollset?.TopRollDiameter ?? null,
				standId: rollset?.TopRollStandId ?? null,
			});
			setInitialBottomFields({
				diameter: rollset?.BottomRollDiameter ?? null,
				standId: rollset?.BottomRollStandId ?? null,
			});

			const runValidation = async () => {
				await trigger();
			};
			runValidation();
		}
	}, [show]);

	return (
		<>
			<MesfModal
				title={`ROLLSET ASSEMBLY`}
				open={show}
				handleClose={() => onHide(false)}
				maxWidth="md"
				id="rollset-assembly-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						sx={{ p: 2, backgroundColor: pageBgColor }}
					>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ md: 4 }}>
								<SectionSelector label="Section" value={sectionId} disabled />
							</Grid2>
							<Grid2 size={{ md: 4 }}>
								<TextField
									fullWidth
									label="Stand"
									variant="outlined"
									size="small"
									value={rollset?.StandName ?? ""}
									disabled
								/>
							</Grid2>
							<Grid2 size={{ md: 4 }}>
								<TextField
									fullWidth
									label="Letter"
									variant="outlined"
									size="small"
									value={rollset?.Letter ?? ""}
									disabled
								/>
							</Grid2>
						</Grid2>

						<Box
							sx={{
								mt: 2,
								display: "flex",
								flexDirection: "row",
								gap: 2,
							}}
						>
							<Box sx={{ flex: 1 }} data-test-id="top">
								<Controller
									name="TopRollId"
									control={control}
									rules={{
										validate: {
											sameRolls: (value, formValues) => {
												return value !== null &&
													value === formValues.BottomRollId
													? "Can't be the same as Bottom Roll"
													: true;
											},
											sameType: (value, formValues) =>
												value !== null &&
												formValues.BottomRollId !== null &&
												formValues.TopRollType !== formValues.BottomRollType
													? "Must be the same type as Bottom Roll"
													: true,
										},
										deps: ["TopRollId", "BottomRollId"],
									}}
									render={({ field, fieldState }) => (
										<RollSelector
											label="Top Roll"
											value={field.value}
											fieldError={fieldState.error}
											sectionId={sectionId}
											standId={rollset?.StandId}
											diameter={topRollDiameter}
											diameterError={getDiameterError({
												topDiameter: topRollDiameter,
												bottomDiameter: bottomRollDiameter,
											})}
											onChange={(id, diameter, standId, type) => {
												field.onChange(id);
												setInitialTopFields({
													diameter: diameter ?? null,
													standId: standId ?? null,
												});
												setValue("TopRollDiameter", diameter ?? 0);
												setValue("TopRollStandId", standId ?? null);
												setValue("TopRollType", type ?? null);
											}}
											onDiameterChange={(diameter) => {
												setValue("TopRollDiameter", diameter ?? 0, {
													shouldValidate: true,
													shouldDirty: true,
												});
											}}
											params={{
												sectionId,
											}}
										/>
									)}
								/>
							</Box>
							<Box sx={{ flex: 1 }} data-test-id="bottom">
								<Controller
									name="BottomRollId"
									control={control}
									rules={{
										validate: {
											sameRolls: (value, formValues) => {
												return value !== null && value === formValues.TopRollId
													? "Can't be the same as Top Roll"
													: true;
											},
											sameType: (value, formValues) =>
												value !== null &&
												formValues.TopRollId !== null &&
												formValues.TopRollType !== formValues.BottomRollType
													? "Must be the same type as Top Roll"
													: true,
										},
										deps: ["TopRollId", "BottomRollId"],
									}}
									render={({ field, fieldState }) => (
										<RollSelector
											label="Bottom Roll"
											value={field.value}
											fieldError={fieldState.error}
											sectionId={sectionId}
											standId={rollset?.StandId}
											diameter={bottomRollDiameter}
											diameterError={getDiameterError({
												topDiameter: topRollDiameter,
												bottomDiameter: bottomRollDiameter,
											})}
											onChange={(id, diameter, standId, type) => {
												field.onChange(id);
												setInitialBottomFields({
													diameter: diameter ?? null,
													standId: standId ?? null,
												});
												setValue("BottomRollDiameter", diameter ?? 0);
												setValue("BottomRollStandId", standId ?? null);
												setValue("BottomRollType", type ?? null);
												trigger("TopRollId");
											}}
											onDiameterChange={(diameter) => {
												setValue("BottomRollDiameter", diameter ?? 0, {
													shouldValidate: true,
													shouldDirty: true,
												});
											}}
											params={{
												sectionId,
											}}
										/>
									)}
								/>
							</Box>
						</Box>
					</MesfModal.Content>
					<MesfModal.Actions sx={{ p: 2 }}>
						<FooterModalButtons
							isSubmitLoading={
								upsertMutation.isPending || upsertRollMutation.isPending
							}
							onHide={onHide}
							disabled={!isFormDirty || !bothRollsDefined}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Updating Rollset"
			/>
			<ConfirmationDialogRaw
				title="CONFIRMATION"
				message={confirmMessage}
				open={confirmMessage !== ""}
				onClose={handleConfirmationClose}
			/>
		</>
	);
};
